import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Table, Button } from "react-bootstrap";
import { format } from "date-fns";
import LoadingSpinner from "../components/misc/LoadingSpinner";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./css/ReportsStyles.scss";
import logo from "../logo.png";
import calculateDueDates from "../components/misc/calculateDueDates";
import ToPeso from "../components/misc/ToPeso";
import GLOBALVAR from "../constants";

function ReportPortfolioAtRiskByAging() {
    const [isLoading, setIsLoading] = useState(false);
    const [endDate, setEndDate] = useState("");
    const [loanData, setLoanData] = useState([]);
    const [groupedLoans, setGroupedLoans] = useState({});

    // Calculate missed terms starting from the last unpaid due date
    const calculateMissedTerms = (dueDates, payments, endDate) => {
        const currentDate = endDate ? new Date(endDate) : new Date(); // Use passed endDate or current date
        
        // Filter due dates up to the currentDate
        const filteredDueDates = dueDates.filter(due => new Date(due.date) <= currentDate);
    
        // Sort due dates in chronological order
        filteredDueDates.sort((a, b) => new Date(a.date) - new Date(b.date));
    
        // Filter payments up to the currentDate
        const filteredPayments = payments.filter(payment => new Date(payment.paymentDate) <= currentDate);
    
        // Sort payments in chronological order
        filteredPayments.sort((a, b) => new Date(a.paymentDate) - new Date(b.paymentDate));
    
        let remainingBalance = 0; // Tracks how much is still due
        let missedTerms = 0;
        let dueIndex = 0; // Tracks the current due date being paid
    
        for (const payment of filteredPayments) {
            let paidAmount = payment.paidPrincipal + payment.paidInterest; // Total paid (excluding penalties and other fees)
    
            while (paidAmount > 0 && dueIndex < filteredDueDates.length) {
                let dueDateObj = filteredDueDates[dueIndex];
                let dueAmount = dueDateObj.dueAmount - remainingBalance; // Remaining due amount
    
                if (paidAmount >= dueAmount) {
                    paidAmount -= dueAmount; // Full payment for this due date
                    remainingBalance = 0; // Reset remaining balance
                    dueIndex++; // Move to the next due date
                } else {
                    remainingBalance += paidAmount; // Partial payment applied
                    paidAmount = 0; // Payment fully used
                }
            }
        }
    
        // Count remaining missed due dates after all payments are processed
        missedTerms = filteredDueDates.length - dueIndex;
    
        return missedTerms;
    };    

    const calculateTotalDueAmount = (dueDates, payments, targetDate) => {
        // Helper function to normalize dates for comparison
        const startOfDay = (date) => {
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);
            return d;
        };

        const targetDateNormalized = startOfDay(targetDate);

        // Get all due dates up to target date and sum their amounts
        const totalDueAmount = dueDates
            .filter(due => startOfDay(due.date) <= targetDateNormalized)
            .reduce((sum, due) => sum + due.dueAmount, 0);

        if (!totalDueAmount) return 0;

        // Get all payments up to the target date
        const relevantPayments = payments
            .filter(payment => startOfDay(payment.paymentDate) <= targetDateNormalized)
            .reduce((sum, payment) => 
                sum + 
                parseFloat(payment.paidPrincipal || 0) + 
                parseFloat(payment.paidInterest || 0) + 
                parseFloat(payment.paidOther || 0), 
            0);

        // Calculate remaining due
        const remainingDue = totalDueAmount - relevantPayments;

        return Math.max(remainingDue, 0); // Ensure non-negative result
    };
    
    const getDaysFromLastDueDateToEndDate = (dueDates, payments, endDate) => {
        // Normalize the end date (ensure it's a Date object)
        const normalizedEndDate = new Date(endDate || Date.now());
    
        // Filter due dates and payments that are on or before the end date
        const validDueDates = dueDates.filter(dueDate => new Date(dueDate.date) <= normalizedEndDate);
        const validPayments = payments.filter(payment => new Date(payment.paymentDate) <= normalizedEndDate);
    
        // Find the last due date that has a payment
        const lastDueDate = [...validDueDates].reverse().find(dueDate =>
            validPayments.some(payment => new Date(payment.paymentDate) <= new Date(dueDate.date))
        );
    
        let referenceDate;
        
        if (lastDueDate) {
            referenceDate = new Date(lastDueDate.date);
        } else if (dueDates.length > 0) {
            // Use the latest available due date as a fallback
            referenceDate = dueDates.reduce((latest, current) =>
                new Date(current.date) > new Date(latest.date) ? current : latest
            );
            referenceDate = new Date(referenceDate.date);
        } else {
            return null; // If no due dates are available at all
        }
    
        // Calculate the difference in days between the reference due date and endDate
        const daysDifference = Math.floor((normalizedEndDate - referenceDate) / (1000 * 60 * 60 * 24));
    
        return daysDifference;
    };    

    const getLastDueDateInReference = (dueDates, payments, endDate) => {
        // Normalize the end date (ensure it's a Date object)
        const normalizedEndDate = new Date(endDate || Date.now());
    
        // Filter due dates and payments that are on or before the end date
        const validDueDates = dueDates.filter(dueDate => new Date(dueDate.date) <= normalizedEndDate);
        const validPayments = payments.filter(payment => new Date(payment.paymentDate) <= normalizedEndDate);
    
        // Find the last due date that has a payment
        const lastDueDate = [...validDueDates].reverse().find(dueDate => 
            validPayments.some(payment => new Date(payment.paymentDate) <= new Date(dueDate.date))
        );
    
        if (lastDueDate) {
            return format(new Date(lastDueDate.date), "MM/dd/yyyy");
        }
    
        // If no last due date with a payment is found, use the last due date from the dueDates array
        if (dueDates.length > 0) {
            const lastAvailableDueDate = dueDates.reduce((latest, current) => 
                new Date(current.date) > new Date(latest.date) ? current : latest
            );
            return format(new Date(lastAvailableDueDate.date), "MM/dd/yyyy");
        }
    
        return null; // If no due dates are available at all
    };    
    
    const getActualDueAmount = (dueDates, endDate) => {
        // Filter due dates that are on or before the end date
        const validDueDates = dueDates.filter(dueDate => new Date(dueDate.date) <= new Date(endDate || Date.now()));
    
        // Get the next due date (or the last one) after filtering
        const nextDueDate = validDueDates.length > 0 ? validDueDates[validDueDates.length - 1] : null;
        
        return nextDueDate ? nextDueDate.dueAmount : 0;
    };
     
    const fetchLoans = useCallback(async (endDate) => {
        setIsLoading(true);
        try {
            const loansResponse = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/fetch/all/byaging`, {
                params: { endDate: endDate || format(new Date(), 'yyyy-MM-dd') },
            });

            const totalsResponse = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/fetch/all/sum-totals`, {
                params: { endDate: endDate || format(new Date(), 'yyyy-MM-dd') },
            });

            const totalLoanedAmount = totalsResponse.data.totalAmountGranted;

            const processedLoans = loansResponse.data.rows.reduce((acc, loan) => {
                const fullName = `${loan.applicant.lastName}, ${loan.applicant.firstName} ${loan.applicant.middleName}`;
                const accountNumber = loan.id.toString().padStart(6, "0");
                const loansNumWithLink = (
                    <a href={`/loans/${loan.id}`}>
                        {accountNumber}
                    </a>
                );
                const fullNameWithLink = (
                    <a href={`/applicant/${loan.applicant.id}`}>
                        {fullName}
                    </a>
                );
                const dueDates = calculateDueDates(
                    loan.dateGranted,
                    loan.loanInterval,
                    loan.loanTerm,
                    loan.amountGranted,
                    loan.interestRate,
                    loan.advanceInterest
                );

                const totalPaid = loan.payments.reduce((sum, payment) => 
                    sum + 
                    parseFloat(payment.paidPrincipal || 0) + 
                    parseFloat(payment.paidInterest || 0) + 
                    parseFloat(payment.paidOther || 0), 
                0);
                const initialInterest = loan.advanceInterest ? parseFloat(loan.amountGranted) * (loan.interestRate / 100) : 0;
                const totalRepayments = parseFloat(loan.amountGranted) + ((parseFloat(loan.amountGranted) * (loan.interestRate / 100)) * loan.loanTerm);
                
                const remainingBalance = totalRepayments - totalPaid - initialInterest;

                const missedTerms = calculateMissedTerms(dueDates, loan.payments, endDate);

                const daysPastDue = getDaysFromLastDueDateToEndDate(dueDates, loan.payments, endDate);

                const currentDate = endDate ? new Date(endDate) : new Date();

                const loanDetails = {
                    clientName: fullNameWithLink,
                    clientNameText: fullName,
                    accountNumber: loansNumWithLink,
                    accountNumberText: accountNumber,
                    missedTerms: missedTerms,
                    loanAmount: parseFloat(loan.amountGranted).toFixed(2),
                    parPercentage: parseFloat(((remainingBalance / totalLoanedAmount) * 100)).toFixed(2),
                    daysPastDue: daysPastDue,
                    dueDate: getLastDueDateInReference(dueDates, loan.payments, endDate),
                    monthlyAmortization: parseFloat(getActualDueAmount(dueDates, endDate)).toFixed(2),
                    totalAmountDue: parseFloat(calculateTotalDueAmount(dueDates, loan.payments, currentDate)).toFixed(2),
                    totalOutstandingBalance: parseFloat(remainingBalance).toFixed(2),
                    agingCategory: getAgingCategory(daysPastDue),
                    loanLedger: loan.loanLedger,
                };

                acc.push(loanDetails);
                return acc;
            }, []);

            const groupedByAgingAndLedger = processedLoans.reduce((acc, loan) => {
                const { agingCategory, loanLedger } = loan;

                if (!acc[agingCategory]) {
                    acc[agingCategory] = {};
                }

                if (!acc[agingCategory][loanLedger]) {
                    acc[agingCategory][loanLedger] = [];
                }

                acc[agingCategory][loanLedger].push(loan);
                return acc;
            }, {});

            const sortedAgingCategories = ["Current", "60-90", "90-180", "180-365", "365+"];
            const sortedGroupedByAgingAndLedger = Object.fromEntries(
                sortedAgingCategories.map(category => [category, groupedByAgingAndLedger[category] || {}])
            );

            setLoanData(processedLoans);
            setGroupedLoans(sortedGroupedByAgingAndLedger);
        } catch (error) {
            console.error("Error fetching loans:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLoans(endDate);
    }, [endDate, fetchLoans]);

    const getAgingCategory = (daysPastDue) => {
        if (daysPastDue <= 60) return "Current";
        if (daysPastDue > 60 && daysPastDue <= 90) return "60-90";
        if (daysPastDue > 90 && daysPastDue <= 180) return "90-180";
        if (daysPastDue > 180 && daysPastDue <= 365) return "180-365";
        return "365+";
    };

    const generatePDF = async () => {
        setIsLoading(true);
        try {
          const doc = new jsPDF("landscape");
      
          // Function to center text in the PDF
          const centerText = (text, y, fontWeight = "normal") => {
            doc.setFont(undefined, fontWeight);
            const pageWidth = doc.internal.pageSize.getWidth();
            const textWidth = doc.getTextDimensions(text).w;
            const textX = (pageWidth - textWidth) / 2;
            doc.text(text, textX, y);
          };
      
          // Function to format dates
          const formatDate = (date) => {
            const d = new Date(date);
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            const year = d.getFullYear();
            return `${month}/${day}/${year}`;
          };
      
          // Add logo to the PDF
          const logoImg = await fetch(logo);
          const logoBlob = await logoImg.blob();
          const logoURL = URL.createObjectURL(logoBlob);
          
          // Adjust logo dimensions to keep width under 2 points with proper aspect ratio
          const logoWidth = 50; // Just under 2 points
          const aspectRatio = 60 / 12.30; // Calculate original aspect ratio
          const logoHeight = logoWidth / aspectRatio; // Maintain aspect ratio

          doc.addImage(logoURL, 'PNG', 10, 3, logoWidth, logoHeight);
      
          // Title and subtitle
          doc.setFontSize(14);
          centerText("Portfolio At Risk - By Aging", 10, "bold");
          if (endDate) {
            doc.setFontSize(12);
            centerText(`As of ${formatDate(endDate)}`, 19);
          }
      
          let currentY = 30;
      
          // Define the sorted aging categories
          const sortedAgingCategories = ["Current", "60-90", "90-180", "180-365", "365+"];
      
          // Loop through each aging category
          sortedAgingCategories.forEach((agingCategory) => {
            if (!groupedLoans[agingCategory]) return;
      
            doc.setFontSize(12);
            centerText(`${agingCategory} Aging Category`, currentY);
            currentY += 10;
      
            // Loop through each ledger within the aging category
            Object.keys(groupedLoans[agingCategory]).forEach((ledger) => {
              const ledgerLoans = groupedLoans[agingCategory][ledger];
      
              // Create table rows for the loans in this ledger
              const tableRows = ledgerLoans.map((loan) => [
                loan.clientNameText,
                loan.accountNumberText,
                loan.missedTerms,
                parseFloat(loan.loanAmount).toFixed(2),
                parseFloat(loan.parPercentage).toFixed(2),
                loan.daysPastDue,
                loan.dueDate,
                parseFloat(loan.monthlyAmortization).toFixed(2),
                parseFloat(loan.totalAmountDue).toFixed(2),
                parseFloat(loan.totalOutstandingBalance).toFixed(2),
                loan.agingCategory,
              ]);
      
              // Calculate summary totals for the ledger
              const totalLoanAmount = ledgerLoans.reduce(
                (sum, loan) => sum + (parseFloat(loan.loanAmount) || 0),
                0
              );
              const totalParPercentage = ledgerLoans.reduce(
                (sum, loan) => sum + (parseFloat(loan.parPercentage) || 0),
                0
              );
              const totalAmountDue = ledgerLoans.reduce(
                (sum, loan) => sum + (parseFloat(loan.totalAmountDue) || 0),
                0
              );
              const totalOutstandingBalance = ledgerLoans.reduce(
                (sum, loan) => sum + (parseFloat(loan.totalOutstandingBalance) || 0),
                0
              );
      
              // Add summary rows to the table
              tableRows.push([
                "Total Loans",
                ledgerLoans.length,
                "",
                totalLoanAmount.toFixed(2),
                totalParPercentage.toFixed(2),
                "",
                "",
                "",
                totalAmountDue.toFixed(2),
                totalOutstandingBalance.toFixed(2),
                "",
              ]);
      
              // Define the header row
              const headerRow = [
                "Client Name",
                "Account Number",
                "No. PAR Amort",
                "Loan Amount",
                "PAR (%)",
                "Days Past Due",
                "Due Date",
                "Monthly Amortization",
                "Total Amount Due",
                "Total Outstanding Balance",
                "Aging Category",
              ];

              const headerRowLedger = [
                ledger,
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
              ];
      
              // Add the header row to the table rows
              tableRows.unshift(headerRowLedger);
      
              // Generate the table using autoTable
              doc.autoTable({
                head: [headerRow],
                body: tableRows,
                startY: currentY,
                didParseCell: (data) => {
                  if (data.row.index === 0) {
                    data.cell.styles.fontStyle = "bold";
                  }
                  // Style the summary row
                  if (data.row.index === tableRows.length - 1) {
                    data.cell.styles.fontStyle = "bold";
                    data.cell.styles.fillColor = [240, 240, 240]; // Light gray background for summary row
                  }
                },
              });
      
              // Update the Y position after the table
              currentY = doc.lastAutoTable.finalY + 10;
            });
          });
      
          // Save the PDF with a timestamp
          const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T")[0];
          doc.save(`Portfolio_At_Risk_By_Aging_${timestamp}.pdf`);
        } catch (error) {
          console.error("Error generating PDF:", error);
        } finally {
          setIsLoading(false);
        }
    };

    return (
        <>
            {isLoading && <LoadingSpinner />}
            <div className="filters-container">
                <div className="date-range-controls">
                    <label htmlFor="endDate">As of: </label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        id="endDate"
                    />
                </div>
                <Button href="#" onClick={generatePDF} className="generate-pdf-button">
                    <i className="fa-solid fa-print"></i> Export
                </Button>
            </div>

            {loanData.length > 0 && (
                <div>
                    {Object.keys(groupedLoans).map((agingCategory) => (
                        <div key={agingCategory}>
                            <h3 className="table-heading">{agingCategory} Aging Category</h3>
                            {Object.keys(groupedLoans[agingCategory]).map((ledger, index) => (
                                <div key={index}>
                                    <h3 className="ledger-title">{ledger}</h3>
                                    <div className="table-responsive">
                                    <Table className="list-container table table-hover">
                                        <thead>
                                            <tr>
                                                <th className="list-header-item">Client Name</th>
                                                <th className="list-header-item">Account Number</th>
                                                <th className="list-header-item">No. PAR Amort</th>
                                                <th className="list-header-item">Loan Amount (₱)</th>
                                                <th className="list-header-item">PAR (%)</th>
                                                <th className="list-header-item">Days Past Due</th>
                                                <th className="list-header-item">Due Date</th>
                                                <th className="list-header-item">Monthly Amortization (₱)</th>
                                                <th className="list-header-item">Total Amount Due (₱)</th>
                                                <th className="list-header-item">Total Outstanding Balance (₱)</th>
                                                <th className="list-header-item">Aging Category</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {groupedLoans[agingCategory][ledger].map((loan, idx) => (
                                                <tr className="list-body" key={idx}>
                                                    <td className="list-body-item">{loan.clientName}</td>
                                                    <td className="list-body-item">{loan.accountNumber}</td>
                                                    <td className="list-body-item">{loan.missedTerms}</td>
                                                    <td className="list-body-item"><ToPeso amount={loan.loanAmount} /></td>
                                                    <td className="list-body-item">{loan.parPercentage}</td>
                                                    <td className="list-body-item">{loan.daysPastDue}</td>
                                                    <td className="list-body-item">{loan.dueDate}</td>
                                                    <td className="list-body-item"><ToPeso amount={loan.monthlyAmortization} /></td>
                                                    <td className="list-body-item"><ToPeso amount={loan.totalAmountDue} /></td>
                                                    <td className="list-body-item"><ToPeso amount={loan.totalOutstandingBalance} /></td>
                                                    <td className="list-body-item">{loan.agingCategory}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                        {(() => {
                                            const totalLoanAmount = groupedLoans[agingCategory][ledger].reduce(
                                                (sum, loan) => sum + (parseFloat(loan.loanAmount) || 0),
                                                0
                                            );
                                            const totalParPercentage = groupedLoans[agingCategory][ledger].reduce(
                                                (sum, loan) => sum + (parseFloat(loan.parPercentage) || 0),
                                                0
                                            );
                                            const totalAmountDue = groupedLoans[agingCategory][ledger].reduce(
                                                (sum, loan) => sum + (parseFloat(loan.totalAmountDue) || 0),
                                                0
                                            );
                                            const totalOutstandingBalance = groupedLoans[agingCategory][ledger].reduce(
                                                (sum, loan) => sum + (parseFloat(loan.totalOutstandingBalance) || 0),
                                                0
                                            );

                                            return (
                                                <tr className="list-body">
                                                    <td className="list-body-item"><b>Total Loans</b></td>
                                                    <td className="list-body-item"><b>{groupedLoans[agingCategory][ledger].length}</b></td>
                                                    <td className="list-body-item">&nbsp;</td>
                                                    <td className="list-body-item"><b><ToPeso amount={totalLoanAmount} /></b></td>
                                                    <td className="list-body-item"><b>{totalParPercentage.toFixed(2)}</b></td>
                                                    <td className="list-body-item">&nbsp;</td>
                                                    <td className="list-body-item">&nbsp;</td>
                                                    <td className="list-body-item">&nbsp;</td>
                                                    <td className="list-body-item"><b><ToPeso amount={totalAmountDue} /></b></td>
                                                    <td className="list-body-item"><b><ToPeso amount={totalOutstandingBalance} /></b></td>
                                                    <td className="list-body-item">&nbsp;</td>
                                                </tr>
                                            );
                                        })()}
                                        </tfoot>
                                    </Table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}

export default ReportPortfolioAtRiskByAging;
